/**
 * Connects game signals to SFX playback for SicBo. Audio is a passive observer:
 * it subscribes to Rive events / GameState changes, so the orchestrator stays
 * unaware of sound.
 *
 * SicBo's common sounds (chip select, place bet, win, lose) are GameState-driven
 * (see main.ts's `state.on(...)` chokepoint), NOT Rive events. The dice-bowl
 * cues, however, ARE Rive events emitted by the .riv — they're mapped in
 * `EVENT_SFX` below and wired by `bindGameSounds`.
 *
 * Finding the right Rive event names: every Rive event is logged by RiveLayer as
 * `[Rive][event:passthrough] <target> <name> <props>`. Map from those.
 */
import type { RiveLayer } from '../rive/riveLayer';
import type { SoundManager } from './soundManager';

// Rive event name → SFX clip base name. The .riv emits these dice-bowl cues.
const EVENT_SFX: Readonly<Record<string, string>> = {
  OpenBowl: 'OpenBowl', // bowl opens / dice reveal
  ShakeBowl: 'ShakeBowl', // bowl shaking
  Move: 'Whoosh', // bowl moves in
};

// DOM/state-driven clips, played from the GameState chokepoint in main.ts.
export const CHIP_SELECT_SFX = 'ClickBetSelect'; // chip select + place bet
export const WIN_SFX = 'YouWin'; // played when the DOM win panel appears (main.ts)
export const LOSE_SFX = 'YouLose'; // played when the DOM lose panel appears (main.ts)

// Distinct clips referenced anywhere — feed to SoundManager.preload().
export const SFX_CLIPS: readonly string[] = Array.from(
  new Set([...Object.values(EVENT_SFX), CHIP_SELECT_SFX, WIN_SFX, LOSE_SFX]),
);

/** Subscribe SFX playback to the relevant Rive events (no-op while empty). */
export function bindGameSounds(rive: RiveLayer, sound: SoundManager): void {
  for (const [event, clip] of Object.entries(EVENT_SFX)) {
    rive.on(event, () => sound.play(clip));
  }
}
